import { useState, type FC } from "react";
import { Button } from "antd-mobile";
import { message } from "antd";
import MobileLayout from "../../components/General/MobileLayout/MobileLayout";
import AssessmentHeader from "../../components/common/AssessmentHeader";
import { useNavigate } from "react-router-dom";

const Addmealpage: FC = () => {
  const navigate = useNavigate();
  const [time, setTime] = useState<string>("");
  const [showTimePicker, setShowTimePicker] = useState<boolean>(false);
  const [selectedTime, setSelectedTime] = useState({
    hour: 12,
    minute: 0,
  });

  // Enhanced validation for time
  const isValidTime = (timeStr: string) => {
    if (!timeStr || timeStr.trim() === "") {
      return false;
    }

    const timeRegex = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$/;
    return timeRegex.test(timeStr.trim());
  };

  // Check for duplicate meal times
  const isDuplicateTime = (timeStr: string, meals: any[]) => {
    return meals.some(meal => meal.time === timeStr.trim());
  };

  // Handle form submission with enhanced validation and error handling
  const handleAddMeal = () => {
    // Validation: Check if time is selected
    if (!time || time.trim() === "") {
      message.error("Please select a time for the meal!");
      return;
    }

    // Validation: Check time format
    if (!isValidTime(time)) {
      message.error("Invalid time format. Please select a valid time!");
      return;
    }

    // Get existing meals with error handling
    let existingMeals: any[] = [];
    try {
      const storedMeals = localStorage.getItem("userMeals");
      existingMeals = storedMeals ? JSON.parse(storedMeals) : [];

      // Ensure it's an array
      if (!Array.isArray(existingMeals)) {
        existingMeals = [];
      }
    } catch (error) {
      message.error("Failed to load existing meals. Please try again!");
      return;
    }

    // Check for duplicate time
    if (isDuplicateTime(time, existingMeals)) {
      message.warning(
        "A meal already exists at this time. Please choose a different time!"
      );
      return;
    }

    // Count only user-added meals for proper numbering
    const userAddedCount = existingMeals.filter(
      (meal: any) => meal.isUserAdded === true
    ).length;
    const mealNumber = userAddedCount + 4; // Start from 4 (after First, Second, Third)

    const mealData = {
      id: Date.now(),
      title: `Custom Meal ${mealNumber}`,
      time: time.trim(),
      timestamp: new Date().toISOString(),
      isUserAdded: true, //  Mark as explicitly user-added meal
    };

    // Add new meal
    existingMeals.push(mealData);

    // Save back to localStorage with error handling
    try {
      localStorage.setItem("userMeals", JSON.stringify(existingMeals));

      // Trigger storage event for EatMealPage to auto-select this meal
      window.dispatchEvent(new Event("storage"));

      // Show success message
      // message.success("Meal added successfully!");

      // Navigate back to EatMealPage after short delay
      setTimeout(() => {
        navigate(-1);
      }, 500);
    } catch (error) {
      message.error(
        "Failed to save meal. Storage might be full. Please try again!"
      );
    }
  };

  const canSubmit = time.trim() !== "" && isValidTime(time);

  return (
    <MobileLayout>
      <div
        className="scrollbar-hide relative mx-auto flex w-full max-w-md flex-col bg-[linear-gradient(180deg,#22360B_0%,#22360B_15%,#171916_30%)]"
        style={{
          minHeight: "100vh",
          height: "100vh",
          maxHeight: "100vh",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {/* Assessment Header */}
        <div className="flex-shrink-0">
          <AssessmentHeader
            step={6}
            totalSteps={12}
            onBack={() => navigate(-1)}
          />
        </div>

        {/* Scrollable Content Area */}
        <div className="w-full flex-1 overflow-y-auto overflow-x-hidden pb-16">
          <div
            className="w-full px-4 pt-8"
            style={{ maxWidth: 380, margin: "0 auto" }}
          >
            {/* Title */}
            <div
              className="mb-8 text-center text-title1 font-bold text-white"
              style={{ fontWeight: 700 }}
            >
              Add Meal
            </div>

            {/* Time Picker Field */}
            <div className="mb-8">
              <div className="mb-2 text-callout font-bold text-white">Time</div>
              <button
                type="button"
                onClick={() => setShowTimePicker(true)}
                className={`w-full rounded-xl border-2 px-4 py-4 text-callout font-bold transition-colors focus:outline-none focus:ring-2 focus:ring-opacity-30 ${
                  time
                    ? "border-[#B9E684] bg-[#2F312E] text-white"
                    : "border-transparent bg-[#2F312E] text-white/70"
                }`}
                style={{
                  textAlign: "left",
                }}
              >
                {time || "Select time (e.g., 8:00 AM)"}
              </button>
            </div>
          </div>
        </div>

        {/* Fixed Next Button at  qs */}
        <div
          className="w-full flex-shrink-0 p-4"
          style={{
            background:
              "linear-gradient(to top, rgba(23, 25, 22, 1) 0%, rgba(23, 25, 22, 0.98) 50%, rgba(23, 25, 22, 0.95) 100%)",
            paddingBottom: "max(16px, env(safe-area-inset-bottom))",
          }}
        >
          <Button
            block
            size="large"
            disabled={!canSubmit}
            style={{
              background: canSubmit ? "#C6F68D" : "#666666",
              color: canSubmit ? "#1A2B06" : "#999999",
              fontWeight: 700,
              borderRadius: 12,
              maxWidth: 380,
              height: 56,
              marginLeft: "auto",
              marginRight: "auto",
              marginBottom: 12,
              border: "none",
              cursor: canSubmit ? "pointer" : "not-allowed",
              opacity: canSubmit ? 1 : 0.6,
            }}
            onClick={() => {
              if (canSubmit) {
                handleAddMeal();
              }
            }}
          >
            Add Meal
          </Button>
        </div>

        {/* Custom Time Picker Modal */}
        {showTimePicker && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center sm:items-center md:items-center"
            style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
            onClick={() => setShowTimePicker(false)}
          >
            <div
              className="w-full max-w-md sm:max-w-sm md:max-w-md"
              onClick={e => e.stopPropagation()}
            >
              <div
                className="rounded-3xl bg-card p-6 sm:rounded-2xl"
                style={{
                  backgroundColor: "#F5F5F5",
                  borderRadius: "16px",
                }}
              >
                {/* Header */}
                <h3
                  className="mb-8 text-left text-title3 font-semibold"
                  style={{
                    color: "#333",
                    fontWeight: 600,
                  }}
                >
                  Set time
                </h3>

                {/* Selected Time Display */}
                <div
                  className="mb-6 flex items-center justify-center gap-3 text-4xl font-medium"
                  style={{
                    color: "#8BC34A",
                  }}
                >
                  <span>{String(selectedTime.hour).padStart(2, "0")}</span>
                  <span style={{ margin: "0 8px" }}>:</span>
                  <span>{String(selectedTime.minute).padStart(2, "0")}</span>
                </div>

                {/* Interactive Picker */}
                <div className="mb-8 flex items-center justify-center gap-12">
                  {/* Hour Column */}
                  <div className="flex flex-col items-center">
                    <button
                      onClick={() =>
                        setSelectedTime(prev => ({
                          ...prev,
                          hour: prev.hour === 23 ? 0 : prev.hour + 1,
                        }))
                      }
                      className="mb-4 flex h-8 w-8 items-center justify-center text-title2 text-muted-foreground hover:text-foreground"
                      style={{
                        background: "none",
                        border: "none",
                      }}
                    >
                      ▲
                    </button>
                    <div
                      className="flex h-12 w-16 items-center justify-center rounded-lg border border-border bg-card text-title1 font-medium"
                    >
                      <span className="text-foreground">
                        {String(selectedTime.hour).padStart(2, "0")}
                      </span>
                    </div>
                    <button
                      onClick={() =>
                        setSelectedTime(prev => ({
                          ...prev,
                          hour: prev.hour === 0 ? 23 : prev.hour - 1,
                        }))
                      }
                      className="mt-4 flex h-8 w-8 items-center justify-center text-title2 text-muted-foreground hover:text-foreground"
                      style={{
                        background: "none",
                        border: "none",
                      }}
                    >
                      ▼
                    </button>
                  </div>

                  {/* Minute Column */}
                  <div className="flex flex-col items-center">
                    <button
                      onClick={() =>
                        setSelectedTime(prev => ({
                          ...prev,
                          minute: prev.minute === 59 ? 0 : prev.minute + 1,
                        }))
                      }
                      className="mb-4 flex h-8 w-8 items-center justify-center text-title2 text-gray-500 hover:text-gray-700"
                      style={{
                        background: "none",
                        border: "none",
                      }}
                    >
                      ▲
                    </button>
                    <div
                      className="flex h-12 w-16 items-center justify-center rounded-lg bg-white text-title1 font-medium"
                      style={{
                        color: "#333",
                        border: "1px solid #E0E0E0",
                      }}
                    >
                      {String(selectedTime.minute).padStart(2, "0")}
                    </div>
                    <button
                      onClick={() =>
                        setSelectedTime(prev => ({
                          ...prev,
                          minute: prev.minute === 0 ? 59 : prev.minute - 1,
                        }))
                      }
                      className="mt-4 flex h-8 w-8 items-center justify-center text-title2 text-gray-500 hover:text-gray-700"
                      style={{
                        background: "none",
                        border: "none",
                      }}
                    >
                      ▼
                    </button>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4">
                  <button
                    className="flex-1 rounded-xl bg-muted py-3 text-muted-foreground"
                    onClick={() => setShowTimePicker(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="flex-1 rounded-xl bg-[#8BC34A] py-3 text-white"
                    onClick={() => {
                      const formattedTime = `${String(selectedTime.hour).padStart(2, "0")}:${String(selectedTime.minute).padStart(2, "0")}`;
                      setTime(formattedTime);
                      setShowTimePicker(false);
                    }}
                  >
                    Set Time
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </MobileLayout>
  );
};

export default Addmealpage;
